import { z } from 'zod';
import { AddressSchema } from './address.js';
import { CustomerSapSystemEnum } from './customer.js';

export const CustomerOrderDocumentType = z.literal('customer_order');
export type CustomerOrderDocumentType = z.infer<typeof CustomerOrderDocumentType>;

export const CustomerOrderStatusEnum = z.enum([
  'extracted',
  'needs_review',
  'ready_for_sap',
  'synced',
  'blocked',
  'error',
]);
export type CustomerOrderStatus = z.infer<typeof CustomerOrderStatusEnum>;

export const CustomerOrderIssueSeverityEnum = z.enum(['blocking', 'warning']);
export type CustomerOrderIssueSeverity = z.infer<typeof CustomerOrderIssueSeverityEnum>;

export const CustomerOrderIssueCodeEnum = z.enum([
  'missing_customer_po',
  'missing_customer_po_date',
  'missing_delivery_address',
  'missing_sap_customer',
  'missing_customer_material',
  'material_not_found',
  'material_multiple',
  'missing_quantity',
  'missing_price_or_margin',
  'payload_invalid',
  'sap_error',
]);
export type CustomerOrderIssueCode = z.infer<typeof CustomerOrderIssueCodeEnum>;

export const CustomerOrderIssueSchema = z.object({
  code: CustomerOrderIssueCodeEnum,
  severity: CustomerOrderIssueSeverityEnum.default('blocking'),
  message: z.string().min(1),
  field: z.string().optional(),
  lineIndex: z.number().int().nonnegative().optional(),
});
export type CustomerOrderIssue = z.infer<typeof CustomerOrderIssueSchema>;

export const CustomerOrderHeaderSchema = z.object({
  customerPoNumber: z.string().min(1),
  customerPoDate: z.string().date(),
  sapCustomerNumber: z.string().min(1).optional(),
  customerName: z.string().min(1).optional(),
  deliveryAddress: AddressSchema,
  requestedDeliveryDate: z.string().date().optional(),
  plant: z.string().min(1).default('1000'),
  salesOrderType: z.string().min(1).default('ZAN1'),
  salesOrganization: z.string().min(1).optional(),
  distributionChannel: z.string().min(1).optional(),
  division: z.string().min(1).optional(),
  currency: z.string().length(3).default('EUR'),
  billingBlock: z.string().min(1).optional(),
  deliveryBlock: z.string().min(1).optional(),
});
export type CustomerOrderHeader = z.infer<typeof CustomerOrderHeaderSchema>;

export const CustomerOrderItemSchema = z.object({
  position: z.string().min(1).optional(),
  customerMaterialNumber: z.string().min(1),
  internalMaterialNumber: z.string().min(1).optional(),
  description: z.string().min(1).optional(),
  quantity: z.number().positive(),
  unit: z.string().min(1),
  requestedDeliveryDate: z.string().date().optional(),
  unitPrice: z.number().nonnegative().optional(),
  pricePer: z.number().positive().optional(),
  netAmount: z.number().nonnegative().optional(),
  marginPercent: z.number().optional(),
  marginAmount: z.number().optional(),
});
export type CustomerOrderItem = z.infer<typeof CustomerOrderItemSchema>;

export const CustomerOrderExtractionSchema = z.object({
  documentType: CustomerOrderDocumentType.default('customer_order'),
  status: CustomerOrderStatusEnum.default('extracted'),
  header: CustomerOrderHeaderSchema,
  items: z.array(CustomerOrderItemSchema).min(1),
  blocks: z.array(CustomerOrderIssueSchema).default([]),
  raw: z.unknown().optional(),
});
export type CustomerOrderExtraction = z.infer<typeof CustomerOrderExtractionSchema>;

export const CustomerOrderSapSyncStatusEnum = z.enum([
  'not_started',
  'blocked',
  'validated',
  'posted',
  'error',
  'skipped',
]);
export type CustomerOrderSapSyncStatus = z.infer<typeof CustomerOrderSapSyncStatusEnum>;

export const CustomerOrderSapLookupStatusEnum = z.enum([
  'not_started',
  'matched',
  'not_found',
  'multiple',
  'error',
  'skipped',
]);
export type CustomerOrderSapLookupStatus = z.infer<typeof CustomerOrderSapLookupStatusEnum>;

export const CustomerOrderSapPayloadStatusEnum = z.enum([
  'not_started',
  'built',
  'blocked',
  'error',
]);
export type CustomerOrderSapPayloadStatus = z.infer<typeof CustomerOrderSapPayloadStatusEnum>;

export const CustomerOrderSapPostingStatusEnum = z.enum([
  'not_started',
  'posted',
  'blocked',
  'error',
  'skipped',
]);
export type CustomerOrderSapPostingStatus = z.infer<typeof CustomerOrderSapPostingStatusEnum>;

export const CustomerOrderMaterialMatchSchema = z.object({
  lineIndex: z.number().int().nonnegative(),
  position: z.string().optional(),
  customerMaterialNumber: z.string(),
  internalMaterialNumber: z.string(),
  description: z.string().optional(),
  plant: z.string().default('1000'),
  matchedBy: z.enum(['knmt', 'explicit_internal_material']),
});
export type CustomerOrderMaterialMatch = z.infer<typeof CustomerOrderMaterialMatchSchema>;

export const CustomerOrderSapPricingElementSchema = z.object({
  ConditionType: z.string().min(1),
  ConditionRateValue: z.number(),
  ConditionCurrency: z.string().length(3).optional(),
  ConditionRateValueUnit: z.string().optional(),
});
export type CustomerOrderSapPricingElement = z.infer<typeof CustomerOrderSapPricingElementSchema>;

export const CustomerOrderSapPayloadItemSchema = z.object({
  SalesOrderItem: z.string().min(1),
  Material: z.string().min(1),
  CustomerMaterial: z.string().min(1),
  RequestedQuantity: z.number().positive(),
  RequestedQuantityUnit: z.string().min(1),
  ProductionPlant: z.string().min(1).default('1000'),
  SalesOrderItemText: z.string().optional(),
  RequestedDeliveryDate: z.string().date().optional(),
  to_PricingElement: z
    .object({
      results: z.array(CustomerOrderSapPricingElementSchema),
    })
    .optional(),
});
export type CustomerOrderSapPayloadItem = z.infer<typeof CustomerOrderSapPayloadItemSchema>;

export const CustomerOrderSapPayloadSchema = z.object({
  SalesOrderType: z.string().min(1).default('ZAN1'),
  SalesOrganization: z.string().min(1).optional(),
  DistributionChannel: z.string().min(1).optional(),
  OrganizationDivision: z.string().min(1).optional(),
  SoldToParty: z.string().min(1),
  PurchaseOrderByCustomer: z.string().min(1),
  CustomerPurchaseOrderDate: z.string().date(),
  TransactionCurrency: z.string().length(3).default('EUR'),
  RequestedDeliveryDate: z.string().date().optional(),
  DeliveryBlockReason: z.string().min(1).optional(),
  BillingBlockReason: z.string().min(1).optional(),
  to_Item: z.object({
    results: z.array(CustomerOrderSapPayloadItemSchema).min(1),
  }),
});
export type CustomerOrderSapPayload = z.infer<typeof CustomerOrderSapPayloadSchema>;

export const CustomerOrderSapSyncSchema = z.object({
  documentType: CustomerOrderDocumentType.default('customer_order'),
  system: CustomerSapSystemEnum.optional(),
  status: CustomerOrderSapSyncStatusEnum.default('not_started'),
  customerLookupStatus: CustomerOrderSapLookupStatusEnum.default('not_started'),
  materialMappingStatus: CustomerOrderSapLookupStatusEnum.default('not_started'),
  payloadStatus: CustomerOrderSapPayloadStatusEnum.default('not_started'),
  postingStatus: CustomerOrderSapPostingStatusEnum.default('not_started'),
  plant: z.string().min(1).default('1000'),
  salesOrderType: z.string().min(1).default('ZAN1'),
  issues: z.array(CustomerOrderIssueSchema).default([]),
  materialMatches: z.array(CustomerOrderMaterialMatchSchema).optional(),
  payload: CustomerOrderSapPayloadSchema.optional(),
  salesOrderNumber: z.string().optional(),
  lastSyncedAt: z.string().datetime().optional(),
  lastError: z.string().optional(),
});
export type CustomerOrderSapSync = z.infer<typeof CustomerOrderSapSyncSchema>;
